'use client';

/**
 * AnimatedGradientBorder
 *
 * A reusable wrapper that adds a rotating conic-gradient border to any child.
 * Keeps the animated border logic DRY — GlassCard and NeonCard can use it
 * internally, and the website portal can wrap any element.
 */

import Box from '@mui/material/Box';
import { keyframes, styled } from '@mui/material/styles';
import type React from 'react';
import { forwardRef } from 'react';
import { gradients } from '../../theme/palette';

// ============================================================================
// Types
// ============================================================================

export interface AnimatedGradientBorderProps {
  /** Gradient colors for the border. @default chroma gradient */
  gradient?: string;
  /** Border thickness in pixels. @default 2 */
  borderWidth?: number;
  /** Rotation speed in seconds. @default 4 */
  speed?: number;
  /** Corner radius in pixels. @default 12 */
  borderRadius?: number;
  /** Children to wrap */
  children: React.ReactNode;
  /** Additional CSS class */
  className?: string;
}

// ============================================================================
// Animations
// ============================================================================

// Animate background-position for cross-browser gradient rotation
const gradientSpin = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// ============================================================================
// Styled Components
// ============================================================================

interface StyledWrapperProps {
  $gradient: string;
  $borderWidth: number;
  $speed: number;
  $borderRadius: number;
}

const Wrapper = styled(Box, {
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<StyledWrapperProps>(({ $gradient, $borderWidth, $speed, $borderRadius }) => ({
  position: 'relative',
  borderRadius: $borderRadius,
  padding: $borderWidth,
  overflow: 'hidden',

  // Animated gradient background (visible as the border)
  background: $gradient,
  backgroundSize: '300% 300%',
  animation: `${gradientSpin} ${$speed}s ease infinite`,

  // The inner content mask
  '& > .animated-border-content': {
    position: 'relative',
    borderRadius: Math.max(0, $borderRadius - $borderWidth),
    overflow: 'hidden',
    zIndex: 1,
  },

  // Reduced motion: disable animation
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
}));

// ============================================================================
// Component
// ============================================================================

export const AnimatedGradientBorder = forwardRef<HTMLDivElement, AnimatedGradientBorderProps>(
  ({ gradient = gradients.chroma, borderWidth = 2, speed = 4, borderRadius = 12, children, className }, ref) => {
    return (
      <Wrapper
        ref={ref}
        $gradient={gradient}
        $borderWidth={borderWidth}
        $speed={speed}
        $borderRadius={borderRadius}
        className={className}
      >
        <div className="animated-border-content">{children}</div>
      </Wrapper>
    );
  },
);

AnimatedGradientBorder.displayName = 'AnimatedGradientBorder';

export default AnimatedGradientBorder;
